import React from "react";
import { Edge, Node, NodeProps, useEdges, useReactFlow } from "reactflow";
import { faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { operationType, selectDataType } from "../../../../../services/types";
import { Main } from "./style";
import { webURL } from "../../../../../services/API";
import { store } from '../../../../../redux/store';
import { addUpdateNodeToQueue, updateNodeValue } from "../../../../../services/method";
// import { Textarea } from "../../../../form/textarea";
// import { Select } from "../../../../form/select";
// import { TextInput } from "../../../../form/textInput";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline, IoSync, IoChevronDown } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
// import { Styled } from "./style";

export const allowedFileTypesDocSearch = [
   'application/pdf', 'application/msword',
   'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
   'text/plain', 'text/csv'
];

// RAG Studio data type
interface RagStudioData {
   id: string;
   isIndexed: boolean;
   indexingStatus: string;
   files: { type: string; name: string; url: string }[];
}

// RAG Entity list item
interface RagEntityItem {
   _id: string;
   name: string;
   description?: string;
   isIndexed: boolean;
   indexingStatus: string;
   files: any[];
   urls: any[];
}

type ragProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function DocSearch({ id, data, setChildMinimize, setChildSettings }: ragProps) {
   const [isDragFile, setIsDragFile] = React.useState(false);
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'With File', refId: 'with_file', isActive: true }, { id: 2, name: 'Excel to database', refId: 'excel_database', isActive: true },
      { id: 3, name: 'On-Premise Database', refId: 'on_prem' }, { id: 4, name: 'Workflow Database', refId: 'workflow_database' },
      { id: 5, name: 'RAG Studio', refId: 'rag_studio' }
   ]);
   const [ragStudioData, setRagStudioData] = React.useState<RagStudioData | null>(null);
   const [ragEntities, setRagEntities] = React.useState<RagEntityItem[]>([]);
   const [isUploading, setIsUploading] = React.useState(false);
   const [isIndexing, setIsIndexing] = React.useState(false);
   const { setNodes } = useReactFlow();
   const inputRef = React.useRef<HTMLInputElement>(null);
   const edges = useEdges();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [isConfigOpen, setIsConfigOpen] = React.useState(false);
   const [urlScrapeMode, setUrlScrapeMode] = React.useState('normal');

   const handleConfigChange = (key: string, value: any) => {
      const currentConfig = data.value?.ragConfig || {};
      updateNodeValue(id, setNodes, { ...data.value, ragConfig: { ...currentConfig, [key]: value } });
   };

   const handleDeleteUrl = (urlToDelete: string) => {
      const currentUrls = data.value?.urls || [];
      updateNodeValue(id, setNodes, { ...data.value, urls: currentUrls.filter((u: any) => (typeof u === 'string' ? u : u.url) !== urlToDelete) });
   };

   // Load RAG Studio data
   const loadRagData = React.useCallback(async () => {
      try {
         const response = await axios.get(`${webURL}/rag`);
         const withFileRag = response.data.find((r: any) => r.type === 'with_file');
         if (withFileRag) {
            setRagStudioData({
               id: withFileRag._id,
               isIndexed: withFileRag.isIndexed || false,
               indexingStatus: withFileRag.indexingStatus || 'idle',
               files: withFileRag.files || []
            });
            // Store RAG ID for backend to use
            if (!data.value?.ragStudioId || data.value.ragStudioId !== withFileRag._id) {
               updateNodeValue(id, setNodes, { ragStudioId: withFileRag._id });
            }
         }
      } catch (err) {
         console.error('Error loading RAG Studio data:', err);
      }
   }, [id, data.value?.ragStudioId, setNodes]);

   // Load all RAG entities for RAG Studio selector
   const loadRagEntities = React.useCallback(async () => {
      try {
         const response = await axios.get(`${webURL}/rag`);
         setRagEntities(response.data || []);
      } catch (err) {
         console.error('Error loading RAG entities:', err);
      }
   }, []);

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === (data.value?.request || requests[0].refId) })));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Load RAG Studio data when With File is selected
   React.useEffect(() => {
      if (data.value?.request === 'with_file') {
         loadRagData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.request, isExpanded]);

    // Load RAG entities when expanded
    React.useEffect(() => {
       if (isExpanded) {
          loadRagEntities();
       }
       // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded]);

   // ---
   React.useEffect(() => {
      addUpdateNodeToQueue(setNodes, (nodes) => nodes.map((node) => {
         if (node.id === id) {
            const value = node.data.value ? { ...node.data.value, request: node.data.value?.request || requests[0].refId } : { request: requests[0].refId };
            return { ...node, data: { ...node.data, value } }
         } else return node;
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [edges.length]);

   // ---
   // const handleSelectRequest = (selectId: number) => {
   //    const refId = requests.find(obj => obj.id === selectId)?.refId;

   //    setRequests((requests) => requests.map((request) => {
   //       if (request.id === selectId) return { ...request, isActive: true }; else return { ...request, isActive: false };
   //    }));
   //    setNodes(nodes => nodes.map(node => {
   //       if (node.id === id) {
   //          return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId } } }
   //       } else return node;
   //    }));
   // }

   // // ---
   // const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
   //    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   // }

   // Handle file upload to RAG Studio
   const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const maxFileSize = 100 * 1024 * 1024; // 100 MB
      let files: File[] = [];

      if (event.type === 'drop') {
         files = Array.from((event as React.DragEvent<HTMLDivElement>).dataTransfer.files);
      } else if (event.type === 'change') {
         const changeEvent = event as React.ChangeEvent<HTMLInputElement>;
         if (changeEvent.target.files) files = Array.from(changeEvent.target.files);
      }

      const validFiles = files.filter((file) => allowedFileTypesDocSearch.includes(file.type) && file.size <= maxFileSize);
      if (!validFiles.length) return;

      setIsUploading(true);
      if (isDragFile) setIsDragFile(false);

      try {
         let ragId: string = ragStudioData?.id || '';

         // Create RAG entry if it doesn't exist
         if (!ragId) {
            const createResponse = await axios.post(`${webURL}/rag`, { type: 'with_file' });
            ragId = createResponse.data._id;
            setRagStudioData({
               id: ragId,
               isIndexed: false,
               indexingStatus: 'idle',
               files: []
            });
            updateNodeValue(id, setNodes, { ragStudioId: ragId });
         }

         // Upload each file: first to storage, then metadata to /rag/file
         for (const file of validFiles) {
            // Step 1: Upload file to storage
            const formData = new FormData();
            formData.append('file', file);
            const uploadResult = await axios.post(`${webURL}/general/uploader`, formData, {
               headers: {
                  'Content-Type': 'multipart/form-data',
                  'x-auth-token': store.getState().user.data?.token
               }
            });

            // Step 2: Add file metadata to RAG
            const newFile = {
               type: file.type,
               name: file.name,
               url: uploadResult.data.url
            };
            await axios.post(`${webURL}/rag/file`, { ragId, file: newFile });
         }

         // Reload data after upload
         await loadRagData();
      } catch (err) {
         console.error('Error uploading files:', err);
      } finally {
         setIsUploading(false);
         if (inputRef.current) inputRef.current.value = '';
      }
   }



   // Handle file delete from RAG Studio
   const handleDeleteFile = async (fileUrl: string) => {
      if (!ragStudioData?.id) return;

      try {
         await axios.delete(`${webURL}/rag/file`, {
            data: { ragId: ragStudioData.id, fileUrl }
         });
         // Reload data after delete
         await loadRagData();
      } catch (err) {
         console.error('Error deleting file:', err);
      }
   }

   // Handle indexing
   const handleStartIndexing = async () => {
      const hasFiles = ragStudioData?.files && ragStudioData.files.length > 0;
      const hasUrls = data.value?.urls && data.value.urls.length > 0;

      if (!hasFiles && !hasUrls) return;

      let ragId = '';
      if (data.value?.request === 'rag_studio') {
         ragId = data.value?.ragCollectionId || '';
         if (!ragId) {
            // Cannot index without selecting a collection in RAG Studio mode
            console.error('No RAG collection selected');
            return;
         }
      } else {
         ragId = ragStudioData?.id || '';
         // Create RAG entry if it doesn't exist (e.g. URL-only indexing)
         if (!ragId) {
            try {
               const createResponse = await axios.post(`${webURL}/rag`, { type: 'unified' });
               ragId = createResponse.data._id;
               setRagStudioData({
                  id: ragId,
                  isIndexed: false,
                  indexingStatus: 'idle',
                  files: []
               });
               updateNodeValue(id, setNodes, { ...data.value, ragStudioId: ragId });
            } catch (err) {
               console.error('Error creating RAG entry:', err);
               return;
            }
         }
      }

      setIsIndexing(true);
      try {
         await axios.post(`${webURL}/rag/index`, {
            ragId: ragId,
            urls: data.value?.urls || [],
            config: data.value?.ragConfig || {}
         });

         // Poll for status
         const pollInterval = setInterval(async () => {
            try {
               const { data: statusData } = await axios.get(`${webURL}/rag/index-status/${ragId}`);
               setRagStudioData(prev => prev ? { ...prev, indexingStatus: statusData.indexingStatus, isIndexed: statusData.indexingStatus === 'completed' } : null);
               if (statusData.indexingStatus === 'completed' || statusData.indexingStatus === 'error') {
                  setIsIndexing(false);
                  clearInterval(pollInterval);
                  await loadRagData();
               }
            } catch (err) {
               clearInterval(pollInterval);
               setIsIndexing(false);
            }
         }, 2000);
      } catch (err) {
         console.error('Error starting indexing:', err);
         setIsIndexing(false);
      }
   }

   // ---
   // const handleFileChange = (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
   //    const maxFileSize = 100 * 1024 * 1024; // 100 MB in bytes
   //    let files: File[] = [];

   //    if (event.type === 'drop') {
   //       files = Array.from((event as React.DragEvent<HTMLDivElement>).dataTransfer.files);

   //    } else if (event.type === 'change') {
   //       const changeEvent = event as React.ChangeEvent<HTMLInputElement>;
   //       if (changeEvent.target.files) files = Array.from(changeEvent.target.files);
   //    }

   //    const arr = files.filter((file) => allowedFileTypesDocSearch.includes(file.type) && file.size <= maxFileSize);
   //    if (arr.length) setNodes((nodes) =>
   //       nodes.map((node) => {
   //          if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, current: node.data.value?.current ? [...node.data.value.current, ...arr] : arr } } };
   //          else return node;
   //       })
   //    );
   //    if (isDragFile) setIsDragFile(false);
   // }

   // ---
   // const handleRemove = (index: number, isFromSave: boolean) => {
   //    if (inputRef.current) inputRef.current.value = '';

   //    if (isFromSave) {
   //       setNodes((nodes) => nodes.map((node) => {
   //          if (node.id === id) {
   //             const arr = [...node.data.value.files];
   //             arr.splice(index, 1);
   //             return { ...node, data: { ...node.data, value: { ...node.data.value, files: arr } } };
   //          } else return node;
   //       }));
   //    } else {
   //       setNodes((nodes) => nodes.map((node) => {
   //          if (node.id === id) {
   //             const arr = [...node.data.value.current];
   //             arr.splice(index, 1);
   //             return { ...node, data: { ...node.data, value: { ...node.data.value, current: arr } } };
   //          } else return node;
   //       }));
   //    }
   // }

   // ---
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: '500px' }}
         >
             {/* Modern Container */}
             <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

               {/* Header with Status */}
               <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7.5px 15px',
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  marginBottom: '16px'
               }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                     <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: ragStudioData?.isIndexed ? '#00d4aa' : isIndexing ? '#ffc107' : '#666'
                     }} />
                     <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                        {ragStudioData?.isIndexed ? 'Ready to Query' : isIndexing ? 'Indexing...' : 'Not Indexed'}
                     </span>
                  </div>
                  <button
                     onClick={handleStartIndexing}
                     disabled={isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length)}
                     style={{
                        padding: '6px 14px',
                        background: isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length)
                           ? 'rgba(255,255,255,0.1)'
                           : 'linear-gradient(135deg, #00d4aa 0%, #00a8cc 100%)',
                        color: isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length) ? '#666' : '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length) ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'all 0.2s ease'
                     }}
                  >
                     {isIndexing ? 'Processing' : 'Index Now'}
                  </button>
               </div>

               <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '4px' }}>

                  {/* RAG Entity Selector */}
                  <div style={{
                     background: '#fff',
                     borderRadius: '10px',
                     border: '1px solid #e8e8e8',
                     marginBottom: '12px',
                     overflow: 'hidden'
                  }}>
                     <div style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid #f0f0f0',
                        background: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                     }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                           RAG Entity
                        </span>
                        <button
                           onClick={() => loadRagEntities()}
                           style={{ background: 'none', border: 'none', fontSize: '11px', color: '#667eea', cursor: 'pointer', fontWeight: 600 }}
                        >↻ Refresh</button>
                     </div>
                     <div style={{ padding: '12px 14px' }}>
                        <select
                           value={data.value?.ragCollectionId || ''}
                           onChange={(e) => {
                              const selectedId = e.target.value;
                              updateNodeValue(id, setNodes, { ...data.value, ragCollectionId: selectedId, request: 'rag_studio' });
                           }}
                           style={{
                              width: '100%',
                              padding: '10px 12px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              fontSize: '13px',
                              outline: 'none',
                              background: '#fafafa',
                              cursor: 'pointer'
                           }}
                        >
                           <option value="">-- Choose a RAG entity --</option>
                           {ragEntities.map((entity) => (
                              <option key={entity._id} value={entity._id}>
                                 {entity.name} {entity.isIndexed ? '✅' : '❌'} ({(entity.files?.length || 0) + (entity.urls?.length || 0)} sources)
                              </option>
                           ))}
                        </select>
                        {data.value?.ragCollectionId && (() => {
                           const selected = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                           if (!selected) return null;
                           return (
                              <div style={{
                                 marginTop: '10px',
                                 padding: '10px 14px',
                                 borderRadius: '8px',
                                 background: selected.isIndexed
                                    ? 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)'
                                    : 'linear-gradient(135deg, #fff3e0 0%, #fbe9e7 100%)',
                                 border: `1px solid ${selected.isIndexed ? '#c8e6c9' : '#ffe0b2'}`
                              }}>
                                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{selected.name}</span>
                                    <span style={{
                                       fontSize: '10px',
                                       padding: '3px 8px',
                                       borderRadius: '10px',
                                       background: selected.isIndexed ? '#4caf50' : '#ff9800',
                                       color: '#fff',
                                       fontWeight: 600
                                    }}>
                                       {selected.isIndexed ? 'Indexed' : 'Not Indexed'}
                                    </span>
                                 </div>
                                 {selected.description && (
                                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{selected.description}</div>
                                 )}
                                 <div style={{ fontSize: '11px', color: '#888' }}>
                                    {selected.files?.length || 0} files · {selected.urls?.length || 0} URLs
                                 </div>
                                 {!selected.isIndexed && (
                                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#e65100', fontWeight: 500 }}>
                                       ⚠ Index this entity in RAG Studio before querying
                                    </div>
                                 )}
                              </div>
                           );
                        })()}
                     </div>
                  </div>

                  {/* Question Card */}
                  <div style={{
                     background: '#fff',
                     borderRadius: '10px',
                     border: '1px solid #e8e8e8',
                     marginBottom: '12px',
                     overflow: 'hidden'
                  }}>
                     <div style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid #f0f0f0',
                        background: '#fafafa'
                     }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                           Question
                        </span>
                     </div>
                     <div style={{ padding: '12px 14px' }}>
                        <textarea
                           value={data.value?.question || ''}
                           onChange={(e) => updateNodeValue(id, setNodes, { ...data.value, question: e.target.value })}
                           placeholder="Enter the question to ask against the documents..."
                           style={{
                              width: '100%',
                              minHeight: '60px',
                              padding: '10px 12px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              fontSize: '13px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              lineHeight: '1.5',
                              background: '#fafafa',
                              boxSizing: 'border-box'
                           }}
                        />
                        <div style={{ marginTop: '6px', fontSize: '11px', color: '#888', lineHeight: '1.4' }}>
                           Supports {'{{variables}}'}. Overridden by Input handle if connected.
                        </div>
                     </div>
                  </div>

                  {/* Instructions Card */}
                  <div style={{
                     background: '#fff',
                     borderRadius: '10px',
                     border: '1px solid #e8e8e8',
                     marginBottom: '12px',
                     overflow: 'hidden'
                  }}>
                     <div style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid #f0f0f0',
                        background: '#fafafa'
                     }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                           System Instructions
                        </span>
                     </div>
                     <div style={{ padding: '12px 14px' }}>
                        <textarea
                           value={data.value?.instructions || ''}
                           onChange={(e) => updateNodeValue(id, setNodes, { ...data.value, instructions: e.target.value })}
                           placeholder="Enter instructions for how the AI should use the retrieved context..."
                           style={{
                              width: '100%',
                              minHeight: '60px',
                              padding: '10px 12px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              fontSize: '13px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              lineHeight: '1.5',
                              background: '#fafafa',
                              boxSizing: 'border-box'
                           }}
                        />
                     </div>
                  </div>

                  {/* Sources Card */}
                  <div style={{
                     background: '#fff',
                     borderRadius: '10px',
                     border: '1px solid #e8e8e8',
                     marginBottom: '12px',
                     overflow: 'hidden'
                  }}>
                     <div style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid #f0f0f0',
                        background: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                     }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                           Sources
                        </span>
                        <span style={{ fontSize: '11px', color: '#888' }}>
                           {(() => {
                              const selectedEntity = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                              const entityFiles = selectedEntity?.files?.length || 0;
                              const entityUrls = selectedEntity?.urls?.length || 0;
                              const nodeFiles = ragStudioData?.files?.length || 0;
                              const nodeUrls = data.value?.urls?.length || 0;
                              return (entityFiles + entityUrls + nodeFiles + nodeUrls) + ' items';
                           })()}
                        </span>
                     </div>

                     {/* File Upload Area */}
                     <div
                        onDragOver={e => { e.preventDefault(); if (!isDragFile) setIsDragFile(true) }}
                        onDragLeave={_ => setIsDragFile(false)}
                        onDrop={handleFileUpload}
                        style={{
                           margin: '12px 14px',
                           padding: '10px',
                           border: `2px dashed ${isDragFile ? '#00d4aa' : '#ddd'}`,
                           borderRadius: '8px',
                           background: isDragFile ? 'rgba(0, 212, 170, 0.05)' : '#fafafa',
                           textAlign: 'center',
                           cursor: 'pointer',
                           transition: 'all 0.2s ease',
                           position: 'relative'
                        }}
                     >
                        {isUploading ? (
                           <IoSync size={24} color="#00d4aa" style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                           <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: '24px', color: '#aaa' }} />
                        )}
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                           {isUploading ? 'Uploading...' : 'Drop files here or click to upload'}
                        </div>
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#aaa' }}>
                           PDF, Word, Excel, TXT, CSV
                        </div>
                        <input
                           ref={inputRef}
                           type="file"
                           onChange={handleFileUpload}
                           multiple
                           accept={allowedFileTypesDocSearch.join(',')}
                           disabled={isUploading}
                           style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              opacity: 0,
                              cursor: 'pointer'
                           }}
                        />
                     </div>

                     {/* Files List */}
                     {ragStudioData?.files && ragStudioData.files.length > 0 && (
                        <div style={{ padding: '0 14px 12px' }}>
                           <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: 500 }}>UPLOADED FILES</div>
                           <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '150px', overflowY: 'auto', paddingRight: '4px' }}>
                              {ragStudioData.files.map((file, index) => (
                                 <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px',
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
                                    borderRadius: '6px',
                                    fontSize: '12px'
                                 }}>
                                    <span style={{
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                       whiteSpace: 'nowrap',
                                       maxWidth: '250px',
                                       color: '#333'
                                    }}>
                                       📄 {file.name}
                                    </span>
                                    <button
                                       onClick={() => handleDeleteFile(file.url)}
                                       style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#e74c3c',
                                          cursor: 'pointer',
                                          padding: '2px 6px',
                                          fontSize: '14px',
                                          opacity: 0.7,
                                          transition: 'opacity 0.2s'
                                       }}
                                    >×</button>
                                 </div>
                              ))}
                           </div>
                        </div>
                     )}

                     {/* Entity Files (from selected RAG entity) */}
                     {(() => {
                        const selectedEntity = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                        if (!selectedEntity?.files?.length) return null;
                        return (
                           <div style={{ padding: '0 14px 12px' }}>
                              <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: 500 }}>ENTITY FILES</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '150px', overflowY: 'auto', paddingRight: '4px' }}>
                                 {selectedEntity.files.map((file: any, index: number) => (
                                    <div key={`entity-file-${index}`} style={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       padding: '8px 12px',
                                       background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
                                       borderRadius: '6px',
                                       fontSize: '12px'
                                    }}>
                                       <span style={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          maxWidth: '300px',
                                          color: '#333'
                                       }}>
                                          📄 {file.name || file.url?.split('/').pop() || 'File'}
                                       </span>
                                    </div>
                                 ))}
                              </div>
                           </div>
                        );
                     })()}

                     {/* Entity Scraped URLs (from selected RAG entity) */}
                     {(() => {
                        const selectedEntity = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                        if (!selectedEntity?.urls?.length) return null;
                        return (
                           <div style={{ padding: '0 14px 12px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                              <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: 500 }}>ENTITY URLS</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '150px', overflowY: 'auto', paddingRight: '4px' }}>
                                 {selectedEntity.urls.map((urlItem: any, index: number) => {
                                    const url = typeof urlItem === 'string' ? urlItem : urlItem.url;
                                    return (
                                       <div key={`entity-url-${index}`} style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          padding: '8px 12px',
                                          background: 'linear-gradient(135deg, #e3f2fd 0%, #e1f5fe 100%)',
                                          borderRadius: '6px',
                                          fontSize: '12px'
                                       }}>
                                          <span style={{
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                             whiteSpace: 'nowrap',
                                             maxWidth: '300px',
                                             color: '#0277bd'
                                          }}>
                                             🔗 {url}
                                          </span>
                                       </div>
                                    );
                                 })}
                              </div>
                           </div>
                        );
                     })()}

                     {/* URL Input */}
                     <div style={{ padding: '0 14px 12px', borderTop: ragStudioData?.files?.length ? '1px solid #f0f0f0' : 'none', paddingTop: ragStudioData?.files?.length ? '12px' : '0' }}>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: 500 }}>ADD URLS</div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: data.value?.urls?.length ? '8px' : '0' }}>
                           <select
                              value={urlScrapeMode}
                              onChange={(e) => setUrlScrapeMode(e.target.value as 'normal' | 'vlm')}
                              style={{
                                 padding: '10px',
                                 border: '1px solid #e0e0e0',
                                 borderRadius: '8px',
                                 fontSize: '12px',
                                 background: '#fafafa',
                                 outline: 'none',
                                 cursor: 'pointer',
                                 minWidth: '80px'
                              }}
                           >
                              <option value="normal">Normal</option>
                              <option value="vlm">VLM</option>
                           </select>
                           <input
                              type="text"
                              placeholder="Enter URL and press Enter..."
                              onKeyUp={(e) => {
                                 if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                    const newUrl = e.currentTarget.value.trim();
                                    const currentUrls = data.value?.urls || [];
                                    const exists = currentUrls.some((u: any) => (typeof u === 'string' ? u : u.url) === newUrl);
                                    if (!exists) {
                                       updateNodeValue(id, setNodes, {
                                          ...data.value,
                                          urls: [...currentUrls, {
                                             url: newUrl,
                                             scrapeMode: urlScrapeMode,
                                             scrollCount: urlScrapeMode === 'normal' ? (data.value?.urlScrollCount ?? 7) : undefined,
                                             vlmScreenshots: urlScrapeMode === 'vlm' ? (data.value?.urlVlmScreenshots ?? 10) : undefined
                                          }]
                                       });
                                    }
                                    e.currentTarget.value = '';
                                 }
                              }}
                              style={{
                                 flex: 1,
                                 padding: '10px 12px',
                                 border: '1px solid #e0e0e0',
                                 borderRadius: '8px',
                                 fontSize: '13px',
                                 outline: 'none',
                                 background: '#fafafa',
                                 boxSizing: 'border-box'
                              }}
                           />
                        </div>
                        {/* Scraping Parameters */}
                        {urlScrapeMode === 'normal' && (
                           <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <label style={{ fontSize: '11px', color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>Scrolls</label>
                              <input
                                 type="number"
                                 min={0}
                                 max={50}
                                 value={data.value?.urlScrollCount ?? 7}
                                 onChange={(e) => updateNodeValue(id, setNodes, { ...data.value, urlScrollCount: Math.max(0, Math.min(50, parseInt(e.target.value) || 0)) })}
                                 style={{
                                    width: '60px', padding: '6px 8px', border: '1px solid #e0e0e0', borderRadius: '6px',
                                    fontSize: '12px', background: '#fafafa', outline: 'none', textAlign: 'center',
                                    boxSizing: 'border-box'
                                 }}
                              />
                              <span style={{ fontSize: '10px', color: '#aaa' }}>0–50</span>
                           </div>
                        )}
                        {urlScrapeMode === 'vlm' && (
                           <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <label style={{ fontSize: '11px', color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>Screenshots</label>
                              <input
                                 type="number"
                                 min={1}
                                 max={50}
                                 value={data.value?.urlVlmScreenshots ?? 10}
                                 onChange={(e) => updateNodeValue(id, setNodes, { ...data.value, urlVlmScreenshots: Math.max(1, Math.min(50, parseInt(e.target.value) || 1)) })}
                                 style={{
                                    width: '60px', padding: '6px 8px', border: '1px solid #e0e0e0', borderRadius: '6px',
                                    fontSize: '12px', background: '#fafafa', outline: 'none', textAlign: 'center',
                                    boxSizing: 'border-box'
                                 }}
                              />
                              <span style={{ fontSize: '10px', color: '#aaa' }}>1–50</span>
                           </div>
                        )}
                        {data.value?.urls && data.value.urls.length > 0 && (
                           <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '100px', overflowY: 'auto', paddingRight: '4px' }}>
                              {data.value.urls.map((item: any, index: number) => {
                                 const url = typeof item === 'string' ? item : item.url;
                                 const mode = typeof item === 'string' ? 'normal' : (item.scrapeMode || 'normal');
                                 return (
                                    <div key={index} style={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'space-between',
                                       padding: '8px 12px',
                                       background: 'linear-gradient(135deg, #e3f2fd 0%, #e1f5fe 100%)',
                                       borderRadius: '6px',
                                       fontSize: '12px'
                                    }}>
                                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                          <span style={{
                                             fontSize: '10px',
                                             padding: '2px 6px',
                                             borderRadius: '4px',
                                             background: mode === 'vlm' ? '#7d3c98' : '#0288d1',
                                             color: '#fff',
                                             fontWeight: 600,
                                             textTransform: 'uppercase'
                                          }}>
                                             {mode === 'normal' ? 'Normal' : 'VLM'}
                                          </span>
                                          <span style={{
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                             whiteSpace: 'nowrap',
                                             maxWidth: '200px',
                                             color: '#0277bd'
                                          }}>
                                             {url}
                                          </span>
                                       </div>
                                       <button
                                          onClick={() => handleDeleteUrl(url)}
                                          style={{
                                             background: 'none',
                                             border: 'none',
                                             color: '#e74c3c',
                                             cursor: 'pointer',
                                             padding: '2px 6px',
                                             fontSize: '14px',
                                             opacity: 0.7,
                                             transition: 'opacity 0.2s'
                                          }}
                                       >×</button>
                                    </div>
                                 );
                              })}
                           </div>
                        )}
                     </div>
                  </div>

                  {/* RAG Configuration Card */}
                  <div style={{
                     background: '#fff',
                     borderRadius: '10px',
                     border: '1px solid #e0e0e0',
                     marginBottom: '10px',
                     overflow: 'hidden'
                  }}>
                     <div
                        onClick={() => setIsConfigOpen(!isConfigOpen)}
                        style={{
                           padding: '12px 14px',
                           background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                           cursor: 'pointer',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'space-between'
                        }}
                     >
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>RAG Configuration</span>
                        <IoChevronDown size={16} color="#333" style={{ transform: isConfigOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }} />
                     </div>
                     {isConfigOpen && (
                        <div style={{ padding: '16px', background: '#fff' }}>
                           {/* Models */}
                           <div style={{ marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              <div>
                                 <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#666', marginBottom: '6px', textTransform: 'uppercase' }}>Embedding Model</label>
                                 <select
                                    value={data.value?.ragConfig?.embeddingModel || 'gemini'}
                                    onChange={(e) => handleConfigChange('embeddingModel', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
                                 >
                                    <option value="gemini">Gemini</option>
                                    <option value="openai">OpenAI</option>
                                 </select>
                              </div>
                              <div>
                                 <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#666', marginBottom: '6px', textTransform: 'uppercase' }}>LLM Model</label>
                                 <select
                                    value={data.value?.ragConfig?.llmModel || 'gemini-2.0-flash'}
                                    onChange={(e) => handleConfigChange('llmModel', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
                                 >
                                    <option value="gemini-2.5-pro-preview-05-06">Gemini 2.5 Pro</option>
                                    <option value="gemini-2.5-flash-preview-04-17">Gemini 2.5 Flash</option>
                                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                                    <option value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite</option>
                                    <option value="gpt-4o">GPT-4o</option>
                                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                                 </select>
                              </div>
                           </div>

                           {/* Chunking */}
                           <div style={{ marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              <div>
                                 <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#666', marginBottom: '6px', textTransform: 'uppercase' }}>Strategy</label>
                                 <select
                                    value={data.value?.ragConfig?.chunkingStrategy || 'recursive'}
                                    onChange={(e) => handleConfigChange('chunkingStrategy', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
                                 >
                                    <option value="recursive">Recursive</option>
                                    <option value="fixed">Fixed Size</option>
                                    <option value="semantic">Semantic</option>
                                 </select>
                              </div>
                              <div>
                                 <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#666', marginBottom: '6px', textTransform: 'uppercase' }}>Retrieval</label>
                                 <select
                                    value={data.value?.ragConfig?.retrievalMethod || 'vector'}
                                    onChange={(e) => handleConfigChange('retrievalMethod', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
                                 >
                                    <option value="vector">Vector Search</option>
                                    <option value="hybrid">Hybrid Search</option>
                                 </select>
                              </div>
                           </div>

                           {/* Params */}
                           <div style={{ marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                              <div>
                                 <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>Top K</label>
                                 <input
                                    type="number"
                                    value={data.value?.ragConfig?.topK || 5}
                                    onChange={(e) => handleConfigChange('topK', parseInt(e.target.value))}
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
                                 />
                              </div>
                              <div>
                                 <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>Chunk Size</label>
                                 <input
                                    type="number"
                                    value={data.value?.ragConfig?.chunkSize || 1000}
                                    onChange={(e) => handleConfigChange('chunkSize', parseInt(e.target.value))}
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
                                 />
                              </div>
                              <div>
                                 <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>Overlap</label>
                                 <input
                                    type="number"
                                    value={data.value?.ragConfig?.overlap || 200}
                                    onChange={(e) => handleConfigChange('overlap', parseInt(e.target.value))}
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
                                 />
                              </div>
                           </div>

                           {/* Reranker Toggle */}
                           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                 <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>AI Reranker</span>
                              </div>
                              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                                 <input
                                    type="checkbox"
                                    checked={data.value?.ragConfig?.useReranker ?? true}
                                    onChange={(e) => handleConfigChange('useReranker', e.target.checked)}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                 />
                                 <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: (data.value?.ragConfig?.useReranker ?? true) ? '#667eea' : '#ccc', transition: '0.4s', borderRadius: '24px' }}>
                                    <span style={{ position: 'absolute', content: '""', height: '18px', width: '18px', left: (data.value?.ragConfig?.useReranker ?? true) ? '22px' : '4px', bottom: '3px', backgroundColor: 'white', transition: '0.4s', borderRadius: '50%' }}></span>
                                 </span>
                              </label>
                           </div>
                        </div>
                     )}
                  </div>

               </div>
             </div>
          </ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
export const handleDocSearchRun = async ({ id, nodes, setNodes, edges, isNeedValue }: {
   id: string, nodes: Node<any>[], setNodes: (payload: Node<any>[] | ((nodes: Node<any>[]) => Node<any>[])) => void,
   edges: Edge<any>[], isNeedValue?: boolean, targetHandle?: string
}) => {
   const item = nodes.find(node => node.id === id) as Node<operationType>;
   if (item.data.value?.isTemporaryMemory) return '';

   const fileURLTargets: operationType[] = [];
   const sources: operationType[] = [];

   for (const edge of edges) {
      if (edge.target === item.id) {
         for (const node of nodes) {
            if (node.id === edge.source) {
               if (edge.targetHandle === 'File-URL' || edge.targetHandle === 'File') fileURLTargets.push({ id: node.id, ...node.data });
            }
         }
      } else if (edge.source === item.id) {
         for (const node of nodes) {
            if (node.id === edge.target) {
               sources.push({ id: node.id, ...node.data });
            }
         }
      }
   }
   if (!sources.length && item.data.value?.request !== 'rag_studio') return '';
   let urls: { type: string, name: string, url: string }[] = [];
   let response = '';

   // Removed early return for rag_studio so files from chat can be uploaded first

   if (item.data.value.request === 'with_file' || item.data.value.request === 'excel_database' || item.data.value.request === 'rag_studio') {
      const isInside = !fileURLTargets.some(target => target.outputTypeSub === 'fileURL');
      let arr = item.data.value?.current ? [...item.data.value.current] : [];

      if (!urls.length) {
         if (item.data.value?.files) urls = item.data.value.files.filter((f: any) => f && f.url);
         for (const index in item.data.value.current) {
            const request = new FormData();
            request.append('file', item.data.value.current[index]);
            const URL = (await axios.post(`${webURL}/general/uploader`, request, { headers: { 'Content-Type': 'multipart/form-data', 'x-auth-token': store.getState().user.data?.token } })).data.url;
            urls.push({
               type: item.data.value.current[index].type,
               name: item.data.value.current[index].name,
               url: URL
            });
         }
         item.data.value.current = [];
      }
      arr = [];
      if (!item.data.value) item.data.value = {};

      // Fix state update out of sync issue and repeat run
      item.data.value.files = urls;
      item.data.value.isTemporaryMemory = true;

      addUpdateNodeToQueue(setNodes, (nodes) => nodes.map((node) => {
         if (item.id === node.id) {
            if (isInside) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, files: urls, current: arr, isTemporaryMemory: true } } };
            } return node;
         }
         else if (sources.some(source => source.id === node.id && source.action === 'output')) return { ...node, data: { ...node.data, value: response } };
         else return node;
      }));
      
      if (item.data.value.request === 'rag_studio') {
         if (isNeedValue) return response;
         return;
      }
      if (isNeedValue) return response;
   }
}